/**
 * Contact View
 * Public contact form for support, feedback, and questions
 */

import { SEO_CONTACT } from '@config/seoConfig';
import { useSEO } from '@hooks/useSEO';
import { type ContactFormErrors, contactFormErrors } from '@parlay-picker/shared/contactForm';
import { AlertTriangle, CheckCircle, Mail, MessageSquare, Send } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { apiUrl } from '../config/api';

// =============================================================================
// Types
// =============================================================================

type MessageType = 'issue' | 'comment' | 'feature_request' | 'question';

interface FormData {
  name: string;
  email: string;
  messageType: MessageType;
  message: string;
}

// =============================================================================
// Component
// =============================================================================

export function ContactView(): ReactElement {
  useSEO(SEO_CONTACT);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    messageType: 'question',
    message: '',
  });
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors = contactFormErrors(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(apiUrl('/api/contact/submit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to submit form');
      }

      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        messageType: 'question',
        message: '',
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const messageTypeOptions = [
    { value: 'issue', label: 'Report an Issue', icon: AlertTriangle },
    { value: 'comment', label: 'General Comment', icon: MessageSquare },
    { value: 'feature_request', label: 'Feature Request', icon: Send },
    { value: 'question', label: 'Ask a Question', icon: Mail },
  ];

  return (
    <>
      {/* Fix browser autofill white background */}
      <style>{`
        #contact-form input:-webkit-autofill,
        #contact-form input:-webkit-autofill:hover,
        #contact-form input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px var(--theme-bg) inset !important;
          -webkit-text-fill-color: var(--theme-text) !important;
          box-shadow: 0 0 0 1000px var(--theme-bg) inset !important;
        }
      `}</style>
      <div className="min-h-screen bg-theme-bg">
        <div className="max-w-3xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-theme-text mb-2">Contact Us</h1>
            <p className="text-theme-text-secondary">
              Have a question, feedback, or need help? We'd love to hear from you.
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-theme-success/10 border border-theme-success rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-theme-success flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-theme-success">Message sent successfully!</p>
                <p className="text-sm text-theme-text-secondary mt-1">
                  Thank you for contacting us. We'll get back to you as soon as possible.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {submitError && (
            <div className="mb-6 p-4 bg-theme-danger/10 border border-theme-danger rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-theme-danger flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-theme-danger">Failed to send message</p>
                <p className="text-sm text-theme-text-secondary mt-1">{submitError}</p>
              </div>
            </div>
          )}

          {/* Contact Form */}
          <div className="bg-theme-surface rounded-xl border border-theme-border p-6">
            <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-theme-text mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  data-testid="contact-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-4 py-2 bg-theme-bg border ${errors.name ? 'border-theme-danger' : 'border-theme-border'} rounded-lg text-theme-text placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-theme-primary`}
                  placeholder="Your name"
                  disabled={submitting}
                />
                {errors.name && <p className="text-sm text-theme-danger mt-1">{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-theme-text mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  data-testid="contact-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full px-4 py-2 bg-theme-bg border ${errors.email ? 'border-theme-danger' : 'border-theme-border'} rounded-lg text-theme-text placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-theme-primary`}
                  placeholder="your.email@example.com"
                  disabled={submitting}
                />
                {errors.email && <p className="text-sm text-theme-danger mt-1">{errors.email}</p>}
              </div>

              {/* Message Type */}
              <div>
                <label className="block text-sm font-medium text-theme-text mb-3">
                  Message Type *
                </label>
                <div className="grid grid-cols-1 @3xl:grid-cols-2 gap-3">
                  {messageTypeOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        data-testid={`contact-type-${option.value}`}
                        onClick={() =>
                          setFormData({ ...formData, messageType: option.value as MessageType })
                        }
                        disabled={submitting}
                        className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors ${
                          formData.messageType === option.value
                            ? 'border-theme-primary bg-theme-primary/10'
                            : 'border-theme-border bg-theme-bg hover:border-theme-border-hover'
                        } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Icon
                          className={`w-5 h-5 ${formData.messageType === option.value ? 'text-theme-primary' : 'text-theme-text-secondary'}`}
                        />
                        <span
                          className={`font-medium ${formData.messageType === option.value ? 'text-theme-text' : 'text-theme-text-secondary'}`}
                        >
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-theme-text mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  data-testid="contact-message-textarea"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={6}
                  className={`w-full px-4 py-2 bg-theme-bg border ${errors.message ? 'border-theme-danger' : 'border-theme-border'} rounded-lg text-theme-text placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-theme-primary resize-none`}
                  placeholder="Tell us more about your question, issue, or feedback..."
                  disabled={submitting}
                />
                {errors.message && (
                  <p className="text-sm text-theme-danger mt-1">{errors.message}</p>
                )}
                <p className="text-xs text-theme-text-tertiary mt-1">
                  {formData.message.length} / 5000 characters
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-theme-primary-fill text-theme-on-primary-fill rounded-lg hover:bg-theme-primary-fill-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Additional Info */}
          <div className="mt-6 text-center text-sm text-theme-text-secondary">
            <p>We typically respond within 24-48 hours.</p>
          </div>
        </div>
      </div>
    </>
  );
}
